<template>
  <div class="source-catalog">
    <!-- Header -->
    <div class="header-section">
      <div class="header-content">
        <h1 class="page-title">
          <i class="fas fa-book-medical"></i>
          Healthcare Regulatory Source Catalog
        </h1>
        <p class="page-description">
          Browse and subscribe to official regulatory data feeds and custom monitoring sources.
        </p>
      </div>
      <div class="header-stats">
        <div class="stat-card">
          <span class="stat-value">{{ globalSources.length }}</span>
          <span class="stat-label">Official</span>
        </div>
        <div class="stat-card">
          <span class="stat-value">{{ catalogStats.total_sources || 0 }}</span>
          <span class="stat-label">Custom</span>
        </div>
        <div class="stat-card primary">
          <span class="stat-value">{{ totalSubscriptions }}</span>
          <span class="stat-label">Subscribed</span>
        </div>
      </div>
    </div>

    <!-- Source Type Tabs -->
    <div class="source-type-tabs">
      <button 
        @click="sourceType = 'global'" 
        :class="{ active: sourceType === 'global' }"
        class="tab-btn"
      >
        <i class="fas fa-landmark"></i>
        Official Sources
        <span class="tab-badge global">{{ globalSources.length }}</span>
      </button>
      <button 
        @click="sourceType = 'custom'" 
        :class="{ active: sourceType === 'custom' }"
        class="tab-btn"
      >
        <i class="fas fa-cogs"></i>
        Custom Sources
        <span class="tab-badge custom">{{ catalogStats.total_sources || 0 }}</span>
      </button>
      <div class="tab-info">
        <i class="fas fa-info-circle"></i>
        <span v-if="sourceType === 'global'">Platform-managed regulatory feeds from official government sources</span>
        <span v-else>Organization-specific scraping and monitoring configurations</span>
      </div>
    </div>

    <!-- Global Sources Section -->
    <div v-if="sourceType === 'global'" class="global-sources-section">
      <div v-if="loadingGlobal" class="loading-state">
        <i class="fas fa-spinner fa-spin fa-2x"></i>
        <p>Loading official sources...</p>
      </div>
      
      <div v-else-if="globalSources.length === 0" class="empty-state">
        <i class="fas fa-database fa-3x"></i>
        <h3>No Official Sources Available</h3>
        <p>Platform-managed regulatory data sources will appear here once configured.</p>
      </div>

      <div v-else class="global-sources-grid">
        <div 
          v-for="source in globalSources" 
          :key="source.id" 
          class="global-source-card"
          :class="{ subscribed: isGlobalSubscribed(source.id) }"
        >
          <div class="source-header">
            <span class="source-type-badge" :class="source.source_type">
              {{ formatSourceType(source.source_type) }}
            </span>
            <span v-if="source.is_active" class="status-badge active">
              <i class="fas fa-circle"></i> Active
            </span>
          </div>
          
          <h4 class="source-name">{{ source.name }}</h4>
          <p class="source-description">{{ source.description }}</p>
          
          <div class="source-stats">
            <span class="stat-item">
              <i class="fas fa-file-alt"></i>
              {{ (source.total_events || source.events_count || 0).toLocaleString() }} events
            </span>
            <span v-if="source.last_updated" class="stat-item">
              <i class="fas fa-clock"></i>
              {{ formatRelativeTime(source.last_updated) }}
            </span>
          </div>

          <div class="source-actions">
            <template v-if="isGlobalSubscribed(source.id)">
              <span class="subscribed-badge">
                <i class="fas fa-check"></i> Subscribed
              </span>
              <router-link to="/data-subscriptions" class="manage-link">
                <i class="fas fa-cog"></i> Manage
              </router-link>
            </template>
            <button 
              v-else
              @click="subscribeToGlobalSource(source)"
              class="btn-subscribe"
              :disabled="subscribingGlobal"
            >
              <i class="fas fa-plus"></i> Subscribe
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Custom Sources Section (Original) -->
    <div v-if="sourceType === 'custom'">
      <!-- Filters -->
    <div class="filters-section">
      <div class="search-box">
        <i class="fas fa-search"></i>
        <input
          v-model="filters.search"
          type="text"
          placeholder="Search sources..."
          @input="debouncedSearch"
        />
        <button v-if="filters.search" @click="clearSearch" class="clear-btn">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="filter-group">
        <select v-model="filters.state" @change="loadSources">
          <option value="">All States</option>
          <option value="FED">🏛️ Federal Only</option>
          <option v-for="state in availableStates" :key="state.code" :value="state.code">
            {{ state.name }}
          </option>
        </select>

        <select v-model="filters.authority" @change="loadSources">
          <option value="">All Authority Areas</option>
          <option v-for="(count, area) in catalogStats.authority_areas" :key="area" :value="area">
            {{ area }} ({{ count }})
          </option>
        </select>

        <select v-model="filters.schedule" @change="loadSources">
          <option value="">All Schedules</option>
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
          <option value="monthly">Monthly</option>
        </select>
      </div>

      <div class="filter-actions">
        <button v-if="selectedSources.length > 0" @click="subscribeToSelected" class="btn btn-primary">
          <i class="fas fa-plus-circle"></i>
          Subscribe to {{ selectedSources.length }} Selected
        </button>
        <button @click="clearFilters" class="btn btn-ghost">
          <i class="fas fa-undo"></i>
          Clear Filters
        </button>
      </div>
    </div>

    <!-- View Toggle -->
    <div class="view-toggle">
      <button @click="viewMode = 'grid'" :class="{ active: viewMode === 'grid' }">
        <i class="fas fa-th"></i> Grid
      </button>
      <button @click="viewMode = 'list'" :class="{ active: viewMode === 'list' }">
        <i class="fas fa-list"></i> List
      </button>
      <button @click="viewMode = 'byState'" :class="{ active: viewMode === 'byState' }">
        <i class="fas fa-map"></i> By State
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <i class="fas fa-spinner fa-spin fa-2x"></i>
      <p>Loading sources...</p>
    </div>

    <!-- Grid View -->
    <div v-else-if="viewMode === 'grid'" class="sources-grid">
      <div
        v-for="source in sources"
        :key="source.id"
        class="source-card"
        :class="{ 
          selected: selectedSources.includes(source.id),
          subscribed: isSubscribed(source.id)
        }"
        @click="toggleSelect(source.id)"
      >
        <div class="source-header">
          <div class="source-state" :class="source.state ? 'state' : 'federal'">
            {{ source.state || '🏛️ FED' }}
          </div>
          <div class="source-authority" :class="getAuthorityClass(source.authority_area)">
            {{ source.authority_area || 'General' }}
          </div>
        </div>

        <h4 class="source-name">{{ source.name }}</h4>
        
        <div class="source-meta">
          <span class="meta-item">
            <i class="fas fa-clock"></i>
            {{ source.default_schedule }}
          </span>
          <span class="meta-item">
            <i class="fas fa-globe"></i>
            {{ source.access_method }}
          </span>
        </div>

        <a 
          v-if="source.base_url && source.base_url.startsWith('http')"
          :href="source.base_url" 
          target="_blank" 
          class="source-url"
          @click.stop
        >
          <i class="fas fa-external-link-alt"></i>
          Visit Source
        </a>

        <div class="source-actions">
          <button 
            v-if="!isSubscribed(source.id)"
            @click.stop="subscribeToSource(source)"
            class="btn-subscribe"
          >
            <i class="fas fa-plus"></i> Subscribe
          </button>
          <span v-else class="subscribed-badge">
            <i class="fas fa-check"></i> Subscribed
          </span>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="sources.length === 0" class="empty-state">
        <i class="fas fa-search fa-3x"></i>
        <h3>No Sources Found</h3>
        <p>Try adjusting your filters or search terms.</p>
      </div>
    </div>

    <!-- List View -->
    <div v-else-if="viewMode === 'list'" class="sources-list">
      <table>
        <thead>
          <tr>
            <th class="select-col">
              <input type="checkbox" @change="toggleSelectAll" :checked="allSelected">
            </th>
            <th>State</th>
            <th>Source Name</th>
            <th>Authority</th>
            <th>Schedule</th>
            <th>Method</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr 
            v-for="source in sources" 
            :key="source.id"
            :class="{ selected: selectedSources.includes(source.id), subscribed: isSubscribed(source.id) }"
          >
            <td class="select-col">
              <input 
                type="checkbox" 
                :checked="selectedSources.includes(source.id)"
                @change="toggleSelect(source.id)"
              >
            </td>
            <td>
              <span class="state-badge" :class="source.state ? 'state' : 'federal'">
                {{ source.state || 'FED' }}
              </span>
            </td>
            <td class="source-name-cell">
              <div class="name">{{ source.name }}</div>
              <a 
                v-if="source.base_url && source.base_url.startsWith('http')"
                :href="source.base_url" 
                target="_blank" 
                class="url-link"
              >
                <i class="fas fa-external-link-alt"></i>
              </a>
            </td>
            <td>
              <span class="authority-badge" :class="getAuthorityClass(source.authority_area)">
                {{ source.authority_area || 'General' }}
              </span>
            </td>
            <td>
              <span class="schedule-badge" :class="source.default_schedule">
                {{ source.default_schedule }}
              </span>
            </td>
            <td>{{ source.access_method }}</td>
            <td>
              <button 
                v-if="!isSubscribed(source.id)"
                @click="subscribeToSource(source)"
                class="btn-small btn-subscribe"
              >
                Subscribe
              </button>
              <span v-else class="subscribed-text">✓</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- By State View -->
    <div v-else-if="viewMode === 'byState'" class="by-state-view">
      <div v-for="(stateData, stateCode) in sourcesByState" :key="stateCode" class="state-section">
        <div class="state-header" @click="toggleStateExpand(stateCode)">
          <h3>
            <span v-if="stateCode === 'FED'" class="federal-icon">🏛️</span>
            {{ stateData.name }}
          </h3>
          <span class="source-count">{{ stateData.count }} sources</span>
          <i :class="expandedStates.includes(stateCode) ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
        </div>
        
        <div v-if="expandedStates.includes(stateCode)" class="state-sources">
          <div 
            v-for="source in stateData.sources" 
            :key="source.id" 
            class="source-row"
          >
            <span class="authority-badge" :class="getAuthorityClass(source.authority_area)">
              {{ source.authority_area || 'General' }}
            </span>
            <span class="source-name">{{ source.name }}</span>
            <span class="schedule">{{ source.default_schedule }}</span>
            <button 
              v-if="!isSubscribed(source.id)"
              @click="subscribeToSource(source)"
              class="btn-small"
            >
              Subscribe
            </button>
            <span v-else class="subscribed-text">✓ Subscribed</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination -->
      <div v-if="sources.length > 0 && viewMode !== 'byState'" class="pagination">
        <span class="showing">
          Showing {{ sources.length }} of {{ totalCount }} sources
        </span>
        <button 
          v-if="hasMore" 
          @click="loadMore" 
          class="btn btn-outline"
          :disabled="loadingMore"
        >
          <i v-if="loadingMore" class="fas fa-spinner fa-spin"></i>
          <span v-else>Load More</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import api from '@/services/api'
import { useToast } from 'vue-toastification'
import { debounce } from 'lodash-es'

export default {
  name: 'SourceCatalog',

  setup() {
    const toast = useToast()

    // State
    const loading = ref(true)
    const loadingMore = ref(false)
    const loadingGlobal = ref(true)
    const subscribingGlobal = ref(false)
    const sources = ref([])
    const globalSources = ref([])
    const globalSubscriptions = ref([])
    const sourcesByState = ref({})
    const catalogStats = ref({})
    const subscriptions = ref([])
    const selectedSources = ref([])
    const expandedStates = ref(['FED', 'CO', 'AZ', 'NV'])  // Default expanded
    
    const sourceType = ref('global')  // 'global' or 'custom'
    const viewMode = ref('grid')
    const totalCount = ref(0)
    const currentPage = ref(1)
    const pageSize = 50
    
    const filters = reactive({
      search: '',
      state: '',
      authority: '',
      schedule: ''
    })

    // Computed
    const availableStates = computed(() => {
      return catalogStats.value.available_states || []
    })

    const hasMore = computed(() => {
      return sources.value.length < totalCount.value
    })

    const allSelected = computed(() => {
      return sources.value.length > 0 && 
             sources.value.every(s => selectedSources.value.includes(s.id))
    })

    const totalSubscriptions = computed(() => {
      return subscriptions.value.length + globalSubscriptions.value.length
    })

    // Methods
    const loadGlobalSources = async () => {
      loadingGlobal.value = true
      try {
        const response = await api.get('/regulatory/global-sources/catalog/')
        // Catalog returns { sources: [...], total_sources, subscribed_count }
        globalSources.value = response.data?.sources || response.data?.results || []
      } catch (error) {
        console.error('Error loading global sources:', error)
        // Try fallback endpoint
        try {
          const response = await api.get('/regulatory/global-sources/')
          globalSources.value = Array.isArray(response.data) 
            ? response.data 
            : (response.data?.results || [])
        } catch (fallbackError) {
          console.error('Fallback also failed:', fallbackError)
          globalSources.value = []
        }
      } finally {
        loadingGlobal.value = false
      }
    }

    const loadGlobalSubscriptions = async () => {
      try {
        const response = await api.get('/regulatory/subscriptions/')
        globalSubscriptions.value = response.data?.results || response.data || []
      } catch (error) {
        console.error('Error loading global subscriptions:', error)
        globalSubscriptions.value = []
      }
    }

    const isGlobalSubscribed = (sourceId) => {
      return globalSubscriptions.value.some(s => s.global_source === sourceId)
    }

    const subscribeToGlobalSource = async (source) => {
      subscribingGlobal.value = true
      try {
        await api.post(`/regulatory/global-sources/${source.id}/subscribe/`)
        toast.success(`Subscribed to ${source.name}`)
        await loadGlobalSubscriptions()
      } catch (error) {
        console.error('Error subscribing to global source:', error)
        toast.error('Failed to subscribe')
      } finally {
        subscribingGlobal.value = false
      }
    }

    const formatSourceType = (type) => {
      const types = {
        'federal_register': 'Federal Register',
        'cms_medicaid': 'CMS Medicaid',
        'regulations_gov': 'Regulations.gov',
        'state_medicaid': 'State Medicaid'
      }
      return types[type] || type
    }

    const formatRelativeTime = (dateStr) => {
      if (!dateStr) return 'Never'
      const date = new Date(dateStr)
      const now = new Date()
      const diffMs = now - date
      const diffMins = Math.floor(diffMs / 60000)
      const diffHours = Math.floor(diffMs / 3600000)
      const diffDays = Math.floor(diffMs / 86400000)

      if (diffMins < 1) return 'Just now'
      if (diffMins < 60) return `${diffMins}m ago`
      if (diffHours < 24) return `${diffHours}h ago`
      if (diffDays < 7) return `${diffDays}d ago`
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    }

    const loadCatalogStats = async () => {
      try {
        const response = await api.get('/data-source-templates/catalog/')
        catalogStats.value = response.data
        totalCount.value = response.data.total_sources
      } catch (error) {
        console.error('Error loading catalog stats:', error)
        toast.error('Failed to load catalog statistics')
      }
    }

    const loadSources = async (reset = true) => {
      try {
        if (reset) {
          loading.value = true
          currentPage.value = 1
          sources.value = []
        }

        const params = {
          limit: pageSize,
          offset: reset ? 0 : sources.value.length
        }

        if (filters.search) params.search = filters.search
        if (filters.state === 'FED') {
          params.federal_only = 'true'
        } else if (filters.state) {
          params.state = filters.state
        }
        if (filters.authority) params.authority_area = filters.authority
        if (filters.schedule) params.schedule = filters.schedule

        const response = await api.get('/data-source-templates/', { params })
        
        if (reset) {
          sources.value = response.data
        } else {
          sources.value.push(...response.data)
        }

        // Update total if available
        if (response.headers && response.headers['x-total-count']) {
          totalCount.value = parseInt(response.headers['x-total-count'])
        }
      } catch (error) {
        console.error('Error loading sources:', error)
        toast.error('Failed to load sources')
      } finally {
        loading.value = false
        loadingMore.value = false
      }
    }

    const loadSourcesByState = async () => {
      try {
        loading.value = true
        const response = await api.get('/data-source-templates/by_state/', {
          params: { limit: 15 }
        })
        sourcesByState.value = response.data
      } catch (error) {
        console.error('Error loading sources by state:', error)
        toast.error('Failed to load sources by state')
      } finally {
        loading.value = false
      }
    }

    const loadSubscriptions = async () => {
      try {
        const response = await api.get('/data-sources/')
        subscriptions.value = response.data.map(s => ({
          id: s.id,
          templateId: s.source_template
        }))
      } catch (error) {
        console.error('Error loading subscriptions:', error)
      }
    }

    const loadMore = async () => {
      loadingMore.value = true
      await loadSources(false)
    }

    const debouncedSearch = debounce(() => {
      loadSources()
    }, 300)

    const clearSearch = () => {
      filters.search = ''
      loadSources()
    }

    const clearFilters = () => {
      filters.search = ''
      filters.state = ''
      filters.authority = ''
      filters.schedule = ''
      loadSources()
    }

    const toggleSelect = (sourceId) => {
      const index = selectedSources.value.indexOf(sourceId)
      if (index === -1) {
        selectedSources.value.push(sourceId)
      } else {
        selectedSources.value.splice(index, 1)
      }
    }

    const toggleSelectAll = () => {
      if (allSelected.value) {
        selectedSources.value = []
      } else {
        selectedSources.value = sources.value.map(s => s.id)
      }
    }

    const toggleStateExpand = (stateCode) => {
      const index = expandedStates.value.indexOf(stateCode)
      if (index === -1) {
        expandedStates.value.push(stateCode)
      } else {
        expandedStates.value.splice(index, 1)
      }
    }

    const isSubscribed = (templateId) => {
      return subscriptions.value.some(s => s.templateId === templateId)
    }

    const subscribeToSource = async (source) => {
      try {
        await api.post('/data-source-templates/subscribe/', {
          template_ids: [source.id],
          schedule: 'weekly'
        })
        toast.success(`Subscribed to ${source.name}`)
        await loadSubscriptions()
      } catch (error) {
        console.error('Error subscribing:', error)
        toast.error('Failed to subscribe')
      }
    }

    const subscribeToSelected = async () => {
      if (selectedSources.value.length === 0) return

      try {
        await api.post('/data-source-templates/subscribe/', {
          template_ids: selectedSources.value,
          schedule: 'weekly'
        })
        toast.success(`Subscribed to ${selectedSources.value.length} sources`)
        selectedSources.value = []
        await loadSubscriptions()
      } catch (error) {
        console.error('Error subscribing:', error)
        toast.error('Failed to subscribe to selected sources')
      }
    }

    const getAuthorityClass = (authority) => {
      if (!authority) return 'general'
      const normalised = authority.toLowerCase().replace(/\s+/g, '-')
      return normalised
    }

    // Lifecycle
    onMounted(async () => {
      await Promise.all([
        loadGlobalSources(),
        loadGlobalSubscriptions(),
        loadCatalogStats(),
        loadSources(),
        loadSourcesByState(),
        loadSubscriptions()
      ])
    })

    return {
      // State
      loading,
      loadingMore,
      loadingGlobal,
      subscribingGlobal,
      sources,
      globalSources,
      globalSubscriptions,
      sourcesByState,
      catalogStats,
      subscriptions,
      selectedSources,
      expandedStates,
      sourceType,
      viewMode,
      totalCount,
      filters,

      // Computed
      availableStates,
      hasMore,
      allSelected,
      totalSubscriptions,

      // Methods
      loadSources,
      loadMore,
      loadGlobalSources,
      loadGlobalSubscriptions,
      debouncedSearch,
      clearSearch,
      clearFilters,
      toggleSelect,
      toggleSelectAll,
      toggleStateExpand,
      isSubscribed,
      isGlobalSubscribed,
      subscribeToSource,
      subscribeToGlobalSource,
      subscribeToSelected,
      getAuthorityClass,
      formatSourceType,
      formatRelativeTime
    }
  }
}
</script>

<style scoped>
.source-catalog {
  padding: 2rem;
  max-width: 1600px;
  margin: 0 auto;
  font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
  min-height: 100vh;
}

/* Source Type Tabs */
.source-type-tabs {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  background: white;
  padding: 0.5rem;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  flex-wrap: wrap;
}

.tab-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border: none;
  background: transparent;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;
}

.tab-btn:hover {
  background: #f1f5f9;
  color: #334155;
}

.tab-btn.active {
  background: linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%);
  color: white;
}

.tab-btn.active .tab-badge {
  background: rgba(255, 255, 255, 0.2);
  color: white;
}

.tab-badge {
  font-size: 0.75rem;
  padding: 0.2rem 0.5rem;
  border-radius: 10px;
  font-weight: 600;
}

.tab-badge.global {
  background: #dbeafe;
  color: #1e40af;
}

.tab-badge.custom {
  background: #f3e8ff;
  color: #7c3aed;
}

.tab-info {
  margin-left: auto;
  font-size: 0.85rem;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.tab-info i {
  color: #94a3b8;
}

/* Global Sources Section */
.global-sources-section {
  margin-bottom: 2rem;
}

.global-sources-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.25rem;
}

.global-source-card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 2px solid transparent;
  transition: all 0.2s;
}

.global-source-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.global-source-card.subscribed {
  border-color: #86efac;
  background: #f0fdf4;
}

.global-source-card .source-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.source-type-badge {
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.25rem 0.6rem;
  border-radius: 10px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.source-type-badge.federal_register {
  background: #dbeafe;
  color: #1e40af;
}

.source-type-badge.cms_medicaid {
  background: #d1fae5;
  color: #047857;
}

.source-type-badge.regulations_gov {
  background: #fef3c7;
  color: #92400e;
}

.source-type-badge.state_medicaid {
  background: #fce7f3;
  color: #9d174d;
}

.status-badge {
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  gap: 4px;
  color: #059669;
}

.status-badge i {
  font-size: 6px;
}

.global-source-card .source-name {
  font-size: 1.1rem;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 0.5rem 0;
  line-height: 1.3;
}

.source-description {
  font-size: 0.875rem;
  color: #64748b;
  line-height: 1.5;
  margin: 0 0 1rem 0;
}

.global-source-card .source-stats {
  display: flex;
  gap: 1.25rem;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e2e8f0;
}

.stat-item {
  font-size: 0.8rem;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.stat-item i {
  color: #94a3b8;
}

.global-source-card .source-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.manage-link {
  font-size: 0.8rem;
  color: #64748b;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.3rem;
}

.manage-link:hover {
  color: #0d9488;
}

/* Header */
.header-section {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  padding: 2rem;
  background: linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%);
  border-radius: 16px;
  color: white;
}

.page-title {
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.page-title i {
  color: #5eead4;
}

.page-description {
  color: #94a3b8;
  font-size: 1rem;
  max-width: 600px;
}

.header-stats {
  display: flex;
  gap: 1rem;
}

.stat-card {
  background: rgba(255, 255, 255, 0.1);
  padding: 1rem 1.5rem;
  border-radius: 12px;
  text-align: center;
  backdrop-filter: blur(10px);
}

.stat-card.primary {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.stat-value {
  display: block;
  font-size: 1.75rem;
  font-weight: 700;
}

.stat-label {
  display: block;
  font-size: 0.75rem;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.stat-card.primary .stat-label {
  color: rgba(255, 255, 255, 0.8);
}

/* Filters */
.filters-section {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
  margin-bottom: 1.5rem;
  padding: 1.5rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.search-box {
  position: relative;
  flex: 1;
  min-width: 250px;
}

.search-box i {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
}

.search-box input {
  width: 100%;
  padding: 0.75rem 2.5rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.95rem;
  transition: all 0.2s;
}

.search-box input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.clear-btn {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
}

.filter-group {
  display: flex;
  gap: 0.75rem;
}

.filter-group select {
  padding: 0.75rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.9rem;
  min-width: 150px;
  cursor: pointer;
  background: white;
}

.filter-actions {
  display: flex;
  gap: 0.5rem;
}

/* View Toggle */
.view-toggle {
  display: flex;
  gap: 0.25rem;
  margin-bottom: 1.5rem;
  background: white;
  padding: 0.25rem;
  border-radius: 8px;
  width: fit-content;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.view-toggle button {
  padding: 0.5rem 1rem;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 6px;
  color: #64748b;
  font-size: 0.875rem;
  transition: all 0.2s;
}

.view-toggle button.active {
  background: #3b82f6;
  color: white;
}

/* Grid View */
.sources-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.25rem;
}

.source-card {
  background: white;
  border-radius: 12px;
  padding: 1.25rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.2s;
  border: 2px solid transparent;
}

.source-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.source-card.selected {
  border-color: #3b82f6;
  background: #f0f9ff;
}

.source-card.subscribed {
  opacity: 0.7;
}

.source-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.source-state {
  font-weight: 700;
  font-size: 0.875rem;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
}

.source-state.state {
  background: #dbeafe;
  color: #1e40af;
}

.source-state.federal {
  background: #fef3c7;
  color: #92400e;
}

.source-authority {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-weight: 500;
}

.source-authority.medicaid { background: #d1fae5; color: #047857; }
.source-authority.public-health { background: #dbeafe; color: #1d4ed8; }
.source-authority.insurance { background: #ede9fe; color: #6d28d9; }
.source-authority.licensing { background: #fef3c7; color: #b45309; }
.source-authority.facilities { background: #fee2e2; color: #b91c1c; }
.source-authority.general-law { background: #e2e8f0; color: #475569; }
.source-authority.general { background: #f1f5f9; color: #64748b; }

.source-name {
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.75rem;
  line-height: 1.4;
}

.source-meta {
  display: flex;
  gap: 1rem;
  font-size: 0.8rem;
  color: #64748b;
  margin-bottom: 0.75rem;
}

.meta-item i {
  margin-right: 0.25rem;
}

.source-url {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.8rem;
  color: #3b82f6;
  text-decoration: none;
  margin-bottom: 0.75rem;
}

.source-url:hover {
  text-decoration: underline;
}

.source-actions {
  margin-top: 1rem;
  padding-top: 0.75rem;
  border-top: 1px solid #e2e8f0;
}

.btn-subscribe {
  width: 100%;
  padding: 0.5rem;
  border: none;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s;
}

.btn-subscribe:hover {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
}

.subscribed-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  color: #10b981;
  font-weight: 500;
  font-size: 0.875rem;
}

/* List View */
.sources-list {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.sources-list table {
  width: 100%;
  border-collapse: collapse;
}

.sources-list th {
  background: #f8fafc;
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  font-size: 0.875rem;
  color: #475569;
  border-bottom: 2px solid #e2e8f0;
}

.sources-list td {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #f1f5f9;
  font-size: 0.9rem;
}

.sources-list tr:hover {
  background: #f8fafc;
}

.sources-list tr.selected {
  background: #f0f9ff;
}

.select-col {
  width: 40px;
  text-align: center;
}

.state-badge {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-weight: 600;
  font-size: 0.8rem;
}

.state-badge.state {
  background: #dbeafe;
  color: #1e40af;
}

.state-badge.federal {
  background: #fef3c7;
  color: #92400e;
}

.authority-badge {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
}

.schedule-badge {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
}

.schedule-badge.daily { background: #fee2e2; color: #b91c1c; }
.schedule-badge.weekly { background: #fef3c7; color: #b45309; }
.schedule-badge.monthly { background: #d1fae5; color: #047857; }

.source-name-cell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.source-name-cell .name {
  flex: 1;
}

.url-link {
  color: #3b82f6;
  font-size: 0.75rem;
}

/* By State View */
.by-state-view {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.state-section {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.state-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.5rem;
  cursor: pointer;
  transition: background 0.2s;
}

.state-header:hover {
  background: #f8fafc;
}

.state-header h3 {
  flex: 1;
  font-size: 1.1rem;
  font-weight: 600;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.federal-icon {
  font-size: 1.25rem;
}

.source-count {
  font-size: 0.875rem;
  color: #64748b;
}

.state-sources {
  border-top: 1px solid #e2e8f0;
  padding: 0.5rem 0;
}

.source-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1.5rem;
  border-bottom: 1px solid #f1f5f9;
}

.source-row:last-child {
  border-bottom: none;
}

.source-row .source-name {
  flex: 1;
  font-size: 0.9rem;
  margin: 0;
}

.source-row .schedule {
  font-size: 0.8rem;
  color: #64748b;
}

/* Buttons */
.btn {
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;
}

.btn-primary {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
}

.btn-primary:hover {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
}

.btn-outline {
  background: white;
  border: 2px solid #e2e8f0;
  color: #475569;
}

.btn-outline:hover {
  border-color: #3b82f6;
  color: #3b82f6;
}

.btn-ghost {
  background: none;
  border: none;
  color: #64748b;
}

.btn-ghost:hover {
  color: #1e293b;
}

.btn-small {
  padding: 0.25rem 0.75rem;
  font-size: 0.8rem;
  border-radius: 4px;
  background: #3b82f6;
  color: white;
  border: none;
  cursor: pointer;
}

.btn-small.btn-subscribe {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
}

.subscribed-text {
  color: #10b981;
  font-weight: 500;
  font-size: 0.8rem;
}

/* Loading & Empty States */
.loading-state, .empty-state {
  text-align: center;
  padding: 4rem 2rem;
  color: #64748b;
}

.loading-state i, .empty-state i {
  margin-bottom: 1rem;
  color: #94a3b8;
}

.empty-state h3 {
  color: #475569;
  margin-bottom: 0.5rem;
}

/* Pagination */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 2rem;
  padding: 1rem;
}

.showing {
  font-size: 0.875rem;
  color: #64748b;
}

/* Responsive */
@media (max-width: 768px) {
  .source-catalog {
    padding: 1rem;
  }

  .header-section {
    flex-direction: column;
    gap: 1.5rem;
  }

  .header-stats {
    width: 100%;
    justify-content: space-between;
  }

  .filters-section {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-group {
    flex-direction: column;
  }

  .sources-grid {
    grid-template-columns: 1fr;
  }
}
</style>

